import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import dayjs from "dayjs";

import { imgList } from "@constants/imgList";
import {
  MetricDetailLayout,
  MetricFilterControls,
  SummaryBlock,
  useDeviceReport,
  timeColumn,
  numericColumn,
  plainColumn,
} from "@components/MetricDetail";
import type { FetchReport } from "@components/MetricDetail";
import Excel from "@components/Excel";
import Table from "@components/Table";
import ChartStateLanes from "@components/Charts/ChartStateLanes";
import ChartPlaceholder from "@components/Charts/ChartPlaceholder";
import { ModalFilterOperation } from "@components/Modals";
import { displayImage } from "@utils/image";
import { localAnchorStr } from "@utils/date";
import { apiGetReportWindow } from "@apis/reportWindow";
import { toFixedOrDash } from "@utils/string";
import { runReportAggregation } from "@utils/reportTableWorkerClient";
import {
  formatDuration,
  sumOperationUsage,
} from "@utils/deviceReportService";
import { stateInterface } from "@reduxs/interface";

// Inline icons — same set the temperature/power pages use for the download
// button and the table section heading, so the three read identically.
const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const TableIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
    <path d="M3 10h18" />
    <path d="M10 3v18" />
  </svg>
);
const ChartLineIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19l16 0" />
    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
  </svg>
);
// Tabler "adjustments" (filter) glyph — same one the temperature page uses for
// its filter button, so the record/event toggle sits crisp beside the filters.
const AdjustmentsIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#919eab"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 10a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M6 4v4" />
    <path d="M6 12v8" />
    <path d="M10 16a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M12 4v10" />
    <path d="M12 18v2" />
    <path d="M16 7a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M18 4v1" />
    <path d="M18 9v11" />
  </svg>
);

export default function OperationDetail() {
  const filterOpe = useSelector(
    (state: stateInterface) => state.monitoring.filterOpe
  );
  const [dataTemp, setDataTemp] = useState<any>({});
  const [modal, setModal] = useState({
    open: "",
  });
  const getReportOperation: FetchReport = async (
    filterDeviceDetail,
    language,
    id
  ) => {
    const res = await apiGetReportWindow(
      (anchor: string) =>
        `${
          process.env.REACT_APP_API_DOMAIN
        }/monitoring-api/device-report/${id}/operation?summarize=false&dateTime=${anchor}&dateTimeUnit=${filterDeviceDetail.unit}`,
      localAnchorStr(filterDeviceDetail.date, filterDeviceDetail.period),
      filterDeviceDetail.unit,
      language
    );
    if (res.status && res.code === 200) {
      const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
        "YYYY-MM-DD"
      )} ${
        filterDeviceDetail.period.toString().length < 2
          ? `0${filterDeviceDetail.period}:00:00`
          : `${filterDeviceDetail.period}:00:00`
      }`;
      const { results, eventResults } = await runReportAggregation(
        "operation",
        {
          rawResult: res?.data?.data?.result ?? [],
          dateTimeStr,
          unit: filterDeviceDetail.unit,
        }
      );
      setDataTemp({ results, eventResults });
    }
  };
  const {
    t,
    navigate,
    id,
    deviceDetail,
    filterDeviceDetail,
    plan,
    listUnit,
    listPeriod,
    renderSound,
    // months is padlocked here — a month of operation rows cannot come back
  // inside the proxy timeout (see the option comment in useDeviceReport)
} = useDeviceReport(getReportOperation, { lockMonths: true });

  // At-a-glance runtime summary — same shared math as the device-detail quick
  // view (sumOperationUsage over minute-level rows), so the two never drift.
  // Records drive comp/defrost/fan; door counts come from the event rows
  // (second-precision device transitions — door openings can be < 1 min).
  const summary = useMemo(() => {
    const grid: any[] = dataTemp?.results ?? [];
    // data-carrying rows only — grid gap rows (state undefined) must not be
    // counted as "on" by the state-diff sums
    const recordRows = grid.filter((e) => e?.active !== undefined);
    if (recordRows.length === 0) return null;
    const eventRows: any[] = dataTemp?.eventResults ?? [];
    const usage = sumOperationUsage(
      recordRows,
      eventRows,
      filterDeviceDetail.unit
    );
    const first = recordRows[0];
    const last = recordRows[recordRows.length - 1];
    const diffMin = Math.max(
      0,
      dayjs(last?.time).diff(dayjs(first?.time), "minute")
    );
    return { usage, diffMin, from: first?.time ?? "", to: last?.time ?? "" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTemp, filterDeviceDetail.unit]);
  const durationLabel = summary
    ? summary.diffMin >= 60
      ? `${Math.floor(summary.diffMin / 60)} ${t("detail.hour")} ${
          summary.diffMin % 60
        } ${t("detail.minunit")}`
      : `${summary.diffMin} ${t("detail.minunit")}`
    : "";
  const periodLabel = t(
    `detail.period${filterDeviceDetail.unit}` as any
  ) as string;
  const columns = [
    timeColumn(t),
    plainColumn(t, "detail.active", "active"),
    plainColumn(t, "detail.comp", "compressor"),
    plainColumn(t, "detail.fan", "fan"),
    plainColumn(t, "detail.defrost", "defrost"),
    plainColumn(t, "detail.door", "door"),
    plainColumn(t, "detail.light", "light"),
  ];
  const columnsEvent = [
    timeColumn(t),
    plainColumn(t, "detail.changes", "changes"),
    plainColumn(t, "detail.active", "active"),
    plainColumn(t, "detail.actionpower", "active_action"),
    plainColumn(t, "detail.comp", "compressor"),
    plainColumn(t, "detail.fan", "fan"),
    plainColumn(t, "detail.defrost", "defrost"),
    plainColumn(t, "detail.door", "door"),
    plainColumn(t, "detail.light", "light"),
    plainColumn(t, "detail.actionlight", "light_action"),
    numericColumn(t, "detail.roomtemp", "room_temp"),
    numericColumn(t, "detail.realtemp", "real_temp"),
    numericColumn(t, "detail.evaptemp", "evap_temp"),
    numericColumn(t, "detail.realtemp2", "probe2R"),
  ];
  const handleLogic = (on: any, value: any) => {
    let label = "";
    if (on) {
      label = "act";
    } else {
      label = "dea";
    }
    if (value === 1) {
      // return `Manually ${label}`;
      return `${t(`detail.man${label}`)}`;
    } else if (value === 2) {
      // return `Application ${label}`;
      return `${t(`detail.app${label}`)}`;
    } else {
      return "-";
    }
  };

  // ── Change log: human-readable diff of each event row vs the previous one
  // ("ละลายน้ำแข็ง: หยุดทำงาน → ทำงาน · คอมเพรสเซอร์: ทำงาน → หยุดทำงาน") —
  // event rows are full state snapshots, so without the diff the reader has
  // to compare adjacent rows by eye to see WHAT actually changed.
  const STATE_FIELDS: { key: string; label: string; onoff: 1 | 2 }[] = [
    { key: "active", label: "detail.active", onoff: 1 },
    { key: "comp", label: "detail.comp", onoff: 2 },
    { key: "fan", label: "detail.fan", onoff: 2 },
    { key: "defrost", label: "detail.defrost", onoff: 2 },
    { key: "door", label: "detail.door", onoff: 1 },
    { key: "light", label: "detail.light", onoff: 1 },
  ];
  const onOffLabel = (v: any, kind: 1 | 2) =>
    v === undefined || v === null
      ? "-"
      : v
      ? t(`detail.on${kind}`)
      : t(`detail.off${kind}`);
  const eventChanges = (curr: any, prev: any): string => {
    if (!prev) return "-";
    const parts: string[] = [];
    STATE_FIELDS.forEach((f) => {
      if ((curr?.[f.key] ?? null) !== (prev?.[f.key] ?? null)) {
        parts.push(
          `${t(f.label)}: ${onOffLabel(prev?.[f.key], f.onoff)} → ${onOffLabel(
            curr?.[f.key],
            f.onoff
          )}`
        );
      }
    });
    return parts.length ? parts.join(" · ") : "-";
  };

  // One row → display-label mapping shared by the table AND the Excel sheets
  // (was duplicated inline in both) — "-" = no data, per the null-vs-0 rule.
  const stateLabels = (e: any) => ({
    active:
      (e?.active ?? "-") === "-"
        ? "-"
        : e?.active
        ? t("detail.on1")
        : t("detail.off1"),
    compressor:
      (e?.comp ?? "-") === "-"
        ? "-"
        : e?.comp
        ? t("detail.on2")
        : t("detail.off2"),
    fan:
      (e?.fan ?? "-") === "-" ? "-" : e?.fan ? t("detail.on2") : t("detail.off2"),
    defrost:
      (e?.defrost ?? "-") === "-"
        ? "-"
        : e?.defrost
        ? t("detail.on2")
        : t("detail.off2"),
    door:
      (e?.door ?? "-") === "-"
        ? "-"
        : e?.door
        ? t("detail.on1")
        : t("detail.off1"),
    light:
      (e?.light ?? "-") === "-"
        ? "-"
        : e?.light
        ? t("detail.on1")
        : t("detail.off1"),
  });

  // ── Excel: 3 sheets (summary / state records / events), mirroring the
  // temperature page's export — both record types always ship, regardless of
  // the on-page record/event toggle.
  const F = t("detail.xlsfield");
  const V = t("detail.xlsvalue");
  const kv = (label: string, value: any) => ({ [F]: label, [V]: value });
  const excelInfoData = summary
    ? [
        kv(t("detail.name"), deviceDetail?.deviceInfo?.name ?? "-"),
        kv(
          t("detail.mac"),
          deviceDetail?.controlInfo?.macAddress ??
            deviceDetail?.deviceInfo?.macAddress ??
            "-"
        ),
        kv(t("monitoring.location"), deviceDetail?.name ?? "-"),
        kv(t("detail.reporttype"), periodLabel),
        kv(
          t("detail.dataperiod"),
          `${dayjs(summary.from).format("DD/MM/YYYY HH:mm")} - ${dayjs(
            summary.to
          ).format("DD/MM/YYYY HH:mm")} (${durationLabel})`
        ),
        kv(
          t("detail.comp"),
          `${formatDuration(summary.usage.compressorMs)} ${t("detail.hour")}`
        ),
        kv(
          t("detail.defrost"),
          `${formatDuration(summary.usage.defrostMs)} ${t("detail.hour")}`
        ),
        kv(
          t("detail.fan"),
          `${formatDuration(summary.usage.fanMs)} ${t("detail.hour")}`
        ),
        kv(
          t("detail.door"),
          `${summary.usage.doorOpenCount} ${t("detail.times")} (${formatDuration(
            summary.usage.doorOpenMs
          )} ${t("detail.hour")})`
        ),
        kv(t("detail.printedat"), dayjs().format("DD/MM/YYYY HH:mm")),
      ]
    : [];
  const excelRecordHeader = [
    t("detail.time"),
    t("detail.active"),
    t("detail.comp"),
    t("detail.fan"),
    t("detail.defrost"),
    t("detail.door"),
    t("detail.light"),
  ];
  const excelRecordData = (dataTemp?.results ?? []).map((e: any) => {
    const s = stateLabels(e);
    return {
      Time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
      Power: s.active,
      Compressor: s.compressor,
      Fan: s.fan,
      Defrost: s.defrost,
      Door: s.door,
      Light: s.light,
    };
  });
  const excelEventHeader = [
    t("detail.time"),
    t("detail.changes"),
    t("detail.active"),
    t("detail.actionpower"),
    t("detail.comp"),
    t("detail.fan"),
    t("detail.defrost"),
    t("detail.door"),
    t("detail.light"),
    t("detail.actionlight"),
    t("detail.roomtemp"),
    t("detail.realtemp"),
    t("detail.evaptemp"),
    t("detail.realtemp2"),
  ];
  const eventRowsAll: any[] = dataTemp?.eventResults ?? [];
  const excelEventData = eventRowsAll.map((e: any, i: number) => {
    const s = stateLabels(e);
    return {
      Time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm:ss"),
      Changes: eventChanges(e, eventRowsAll[i - 1]),
      Power: s.active,
      active_action: handleLogic(e?.active === true, e?.powerLastAct ?? ""),
      Compressor: s.compressor,
      Fan: s.fan,
      Defrost: s.defrost,
      Door: s.door,
      Light: s.light,
      light_action: handleLogic(e?.light === true, e?.lightLastAct ?? ""),
      "Room Temp": toFixedOrDash(e?.insideTemp),
      "Real Temp (P1)": toFixedOrDash(e?.realTemp),
      "Evap Temp": toFixedOrDash(e?.evapCoil),
      "Real Temp (P2)": toFixedOrDash(e?.probe2R),
    };
  });
  const excelSheets = [
    { name: t("detail.xlsinfo"), data: excelInfoData },
    {
      name: t("detail.xlsoperecords"),
      header: excelRecordHeader,
      data: excelRecordData,
    },
    {
      name: t("detail.xlsopeevents"),
      header: excelEventHeader,
      data: excelEventData,
    },
  ];

  // Hover summaries for the lane charts: each card explains only ITS OWN lanes
  // (so the control card never shows cooling reasons and vice-versa), in plain
  // language using the interlock the owner described — power is the master;
  // defrost forces compressor + fan off; an open door can stop the fan;
  // otherwise the compressor cuts on the setpoint/differential. Each line
  // carries the colour of its lane so the eye links reason ↔ timeline.
  const C = {
    power: "#3b82f6",
    comp: "#38bdf8",
    defrost: "#f97316",
    fan: "#c026d3",
    light: "#f59e0b",
    door: "#65a30d",
    none: "#94a3b8",
  };
  const on = (v: any) => v === true;
  const off = (v: any) => v === false;
  const known = (v: any) => v === true || v === false;

  // Cooling cycle card: compressor / defrost / fan.
  const explainCooling = (row: any): { color: string; text: string }[] => {
    if (
      !row ||
      (!known(row.active) &&
        !known(row.comp) &&
        !known(row.fan) &&
        !known(row.defrost))
    ) {
      return [{ color: C.none, text: t("detail.reasonnodata") }];
    }
    if (off(row.active))
      return [{ color: C.power, text: t("detail.reasonpoweroffcooling") }];
    const reasons: { color: string; text: string }[] = [];
    if (on(row.comp))
      reasons.push({ color: C.comp, text: t("detail.reasoncompon") });
    else if (off(row.comp))
      reasons.push({
        color: C.comp,
        text: on(row.defrost)
          ? t("detail.reasoncompoffdefrost")
          : t("detail.reasoncompoffcycle"),
      });
    if (on(row.defrost))
      reasons.push({ color: C.defrost, text: t("detail.reasondefroston") });
    if (on(row.fan))
      reasons.push({ color: C.fan, text: t("detail.reasonfanon") });
    else if (off(row.fan))
      // Fan-off context. Only defrost and door are asserted (they're in the
      // row); otherwise we do NOT claim a single cause — the controller has
      // several fan modes (F0: coil-temp / follows-compressor; F3 + dd/Fd:
      // post-defrost delay) and the device's F0/F3 aren't in the data, so a
      // definite reason would be a guess. (Confirmed: the fan can restart on a
      // different schedule than the compressor, so it is NOT always F0=2.)
      reasons.push({
        color: C.fan,
        text: on(row.defrost)
          ? t("detail.reasonfanoffdefrost")
          : on(row.door)
          ? t("detail.reasonfanoffdoor")
          : t("detail.reasonfanoffconfig"),
      });
    return reasons;
  };

  // Control card: power (master) / light / door. Unlike the cooling card, we do
  // NOT collapse to a single "everything off" line when power is off — light and
  // door are independent of the power button (the light often stays on), so we
  // show each lane's real state.
  const explainControl = (row: any): { color: string; text: string }[] => {
    if (
      !row ||
      (!known(row.active) && !known(row.light) && !known(row.door))
    ) {
      return [{ color: C.none, text: t("detail.reasonnodata") }];
    }
    const reasons: { color: string; text: string }[] = [];
    if (on(row.active))
      reasons.push({ color: C.power, text: t("detail.reasonpoweron") });
    else if (off(row.active))
      reasons.push({ color: C.power, text: t("detail.reasonpoweroffstate") });
    if (on(row.light))
      reasons.push({ color: C.light, text: t("detail.reasonlighton") });
    else if (off(row.light))
      reasons.push({ color: C.light, text: t("detail.reasonlightoff") });
    if (on(row.door))
      reasons.push({ color: C.door, text: t("detail.reasondooropen") });
    else if (off(row.door))
      reasons.push({ color: C.door, text: t("detail.reasondoorclosed") });
    return reasons;
  };

  // Lane charts follow the record/event toggle: record = the 1-min grid, event =
  // the precise transition rows (fall back to the grid if the window has no
  // transitions at all, otherwise the whole chart would be blank). Either way
  // the axis is pinned to the full-window bounds of the record grid so the
  // sparser event dataset still spans the whole report window.
  const gridRows: any[] = dataTemp?.results ?? [];
  const eventRows: any[] = dataTemp?.eventResults ?? [];
  const chartRows =
    filterOpe.recordType === "record"
      ? gridRows
      : eventRows.length
      ? eventRows
      : gridRows;
  const chartDomainStart = gridRows[0]?.time;
  const chartDomainEnd = gridRows[gridRows.length - 1]?.time;

  return (
    <MetricDetailLayout
      title={t("detail.opedetail")}
      metricLabel={t("detail.ope")}
      devicesLabel={t("devices.head")}
      onBack={() => navigate(`/device-detail/${id}`)}
      onBreadcrumbDevices={() => navigate(`/devices`)}
      deviceName={deviceDetail?.deviceInfo?.name ?? ""}
      image={
        (deviceDetail?.deviceInfo?.image ?? "")
          ? displayImage(deviceDetail?.deviceInfo?.image ?? "")
          : imgList.ex
      }
      locationName={deviceDetail?.name ?? ""}
      soundSlot={renderSound(
        deviceDetail?.controlInfo?.macAddress ?? "",
        deviceDetail?.controlInfo?.soundAlarm ?? false,
        deviceDetail?.controlInfo?.control?.alarm ?? false
      )}
      headerActions={
        <Excel
          fileName={`${id}-${t("detail.ope")}-${dayjs().format(
            "YYYYMMDDHHmmss"
          )}`}
          sheets={excelSheets as any}
          button={
            <div
              className="size-14 weight-600 color-black pointer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid #e2e8f0",
                padding: "6px 14px",
                borderRadius: 8,
                whiteSpace: "nowrap",
              }}
            >
              <DownloadIcon />
              {t("detail.downloadexcel")}
            </div>
          }
        />
      }
      breadcrumbActions={
        <>
          <MetricFilterControls
            filterDeviceDetail={filterDeviceDetail}
            plan={plan}
            listUnit={listUnit}
            listPeriod={listPeriod}
          />
          {/* record/event toggle — moved up beside the filters, matching the
              temperature page's filter button placement */}
          <div
            className="detail__filter-btn"
            onClick={() => setModal({ open: "filter_oparetion" })}
          >
            <AdjustmentsIcon />
          </div>
        </>
      }
      headlineExtra={
        summary ? (
          // Mirrors the temperature page's summary chrome (SummaryBlock):
          // runtime tiles + the report-type / recording-period line.
          <SummaryBlock>
            <div className="sum__head">{t("detail.opesummary")}</div>
            <div className="sum__row">
              <div className="sum__center">
                <div className="sum__tiles">
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.comp")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {formatDuration(summary.usage.compressorMs)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.hour")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.defrost")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {formatDuration(summary.usage.defrostMs)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.hour")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.fan")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {formatDuration(summary.usage.fanMs)}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.hour")}
                      </span>
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.door")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {summary.usage.doorOpenCount}{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {t("detail.times")} (
                        {formatDuration(summary.usage.doorOpenMs)}{" "}
                        {t("detail.hour")})
                      </span>
                    </div>
                  </div>
                </div>
                <div className="sum__duration">
                  {t("detail.reporttype")}: {periodLabel}
                  {"  ·  "}
                  {t("detail.dataperiod")}: {durationLabel}
                </div>
              </div>
            </div>
          </SummaryBlock>
        ) : null
      }
      filterBar={
        // Chart section heading (matches the temperature/power pages). The
        // record/event toggle lives beside the filters up top.
        <div className="detail__filter">
          <div
            className="detail__head-2"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <ChartLineIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.opechart")}
            </span>
          </div>
        </div>
      }
      chart={
        // Two lane timelines (Gantt-style) on the RAW 1-min record grid,
        // independent of the record/event table toggle. Grouped by the domain
        // relationship the owner described: the cooling cycle (compressor /
        // defrost / fan — they interlock: defrost on → comp + fan off) and the
        // control group (power master + light + door). A filled bar = on.
        (dataTemp?.results ?? []).length > 0 ? (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              {t("detail.statelegend")}
            </div>
            <div
              style={{
                border: "1px solid #eef1f4",
                borderRadius: 12,
                padding: "18px 20px",
                background: "#fff",
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1f2328",
                  margin: "0 0 14px",
                }}
              >
                {t("detail.opecontrol")}
              </div>
              <ChartStateLanes
                data={chartRows}
                domainStart={chartDomainStart}
                domainEnd={chartDomainEnd}
                explain={explainControl}
                series={[
                  {
                    dataKey: "active",
                    name: t("detail.active"),
                    color: "#3b82f6",
                    onLabel: t("detail.on1"),
                  },
                  {
                    dataKey: "light",
                    name: t("detail.light"),
                    color: "#f59e0b",
                    onLabel: t("detail.on1"),
                  },
                  {
                    dataKey: "door",
                    name: t("detail.door"),
                    color: "#65a30d",
                    onLabel: t("detail.on1"),
                  },
                ]}
              />
            </div>
            <div
              style={{
                border: "1px solid #eef1f4",
                borderRadius: 12,
                padding: "18px 20px",
                background: "#fff",
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1f2328",
                  margin: "0 0 14px",
                }}
              >
                {t("detail.opecooling")}
              </div>
              <ChartStateLanes
                data={chartRows}
                domainStart={chartDomainStart}
                domainEnd={chartDomainEnd}
                explain={explainCooling}
                series={[
                  {
                    dataKey: "comp",
                    name: t("detail.comp"),
                    color: "#38bdf8",
                    onLabel: t("detail.on2"),
                  },
                  {
                    dataKey: "defrost",
                    name: t("detail.defrost"),
                    color: "#f97316",
                    onLabel: t("detail.on2"),
                  },
                  {
                    dataKey: "fan",
                    name: t("detail.fan"),
                    color: "#c026d3",
                    onLabel: t("detail.on2"),
                  },
                ]}
              />
            </div>
          </div>
        ) : (
          <ChartPlaceholder
            loading={!Array.isArray(dataTemp?.results)}
            loadingText={t("detail.loadingdata")}
            emptyText={t("detail.nodatarange")}
          />
        )
      }
      table={
        <div className="detail__table">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "8px 0 16px",
            }}
          >
            <TableIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.opetable")}
            </span>
          </div>
          <Table
            pagination={{
              locale: { items_per_page: t("detail.perpage") },
              showLessItems: true,
              showTotal: (total: number, range: [number, number]) =>
                `${t("detail.showing")} ${range[0].toLocaleString(
                  "en-US"
                )}-${range[1].toLocaleString("en-US")} ${t(
                  "detail.oftotal"
                )} ${total.toLocaleString("en-US")} ${t("detail.rowunit")}`,
            }}
            showSorterTooltip={false}
            columns={filterOpe.recordType === "record" ? columns : columnsEvent}
            dataSource={(filterOpe.recordType === "record"
              ? dataTemp?.results ?? []
              : eventRowsAll
            ).map((e: any, index: number, list: any[]) => {
              const s = stateLabels(e);
              return {
                key: index,
                time: dayjs(e?.time ?? "").format(
                  `DD/MM/YYYY HH:mm${
                    filterOpe.recordType !== "record" ? ":ss" : ""
                  }`
                ),
                // event view: what actually changed vs the previous event
                changes:
                  filterOpe.recordType === "record"
                    ? "-"
                    : eventChanges(e, list[index - 1]),
                active: s.active,
                active_action: handleLogic(
                  e?.active === true,
                  e?.powerLastAct ?? ""
                ),
                light_action: handleLogic(
                  e?.light === true,
                  e?.lightLastAct ?? ""
                ),
                compressor: s.compressor,
                fan: s.fan,
                defrost: s.defrost,
                door: s.door,
                light: s.light,
                room_temp: toFixedOrDash(e?.insideTemp),
                real_temp: toFixedOrDash(e?.realTemp),
                evap_temp: toFixedOrDash(e?.evapCoil),
                probe2R: toFixedOrDash(e?.probe2R),
              };
            })}
          />
        </div>
      }
      modals={
        <ModalFilterOperation
          open={modal.open === "filter_oparetion"}
          onOk={() => {
            setModal({
              ...modal,
              open: "",
            });
          }}
          onClose={() =>
            setModal({
              ...modal,
              open: "",
            })
          }
        />
      }
    />
  );
}
